import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PremiumFeatures = ({ activeFeatures, onToggleFeature, onUpgrade }) => {
  const [selectedFeatures, setSelectedFeatures] = useState(new Set(activeFeatures.map(f => f.id)));

  const premiumFeatures = [
    {
      id: 'featured_listing',
      name: 'Featured Listings',
      description: 'Highlight your listings with premium placement and visual badges',
      icon: 'Star',
      pricing: {
        motorcycle: 2500,
        car: 5000
      },
      benefits: ['Top search results', 'Premium badge', 'Highlighted border', '3x more views'],
      category: 'visibility'
    },
    {
      id: 'top_positioning',
      name: 'Top Positioning',
      description: 'Always appear in the first 3 results for relevant searches',
      icon: 'TrendingUp',
      pricing: {
        motorcycle: 1500,
        car: 3000
      },
      benefits: ['First page guarantee', 'Priority in filters', 'Mobile app priority', '2x more clicks'],
      category: 'visibility'
    },
    {
      id: 'professional_badge',
      name: 'Professional Seller Badge',
      description: 'Display verified professional status to build customer trust',
      icon: 'Shield',
      pricing: {
        motorcycle: 1000,
        car: 2000
      },
      benefits: ['Verified badge', 'Trust indicator', 'Professional profile', 'Customer confidence'],
      category: 'trust'
    },
    {
      id: 'multiple_photos',
      name: 'Extended Photo Gallery',
      description: 'Upload up to 20 high-quality photos per listing',
      icon: 'Camera',
      pricing: {
        motorcycle: 800,
        car: 1500
      },
      benefits: ['20 photos max', 'HD quality', 'Zoom functionality', 'Better conversion'],
      category: 'media'
    },
    {
      id: 'urgent_listing',
      name: 'Urgent Sale Badge',
      description: 'Mark listings as urgent with special visual indicators',
      icon: 'Zap',
      pricing: {
        motorcycle: 500,
        car: 1000
      },
      benefits: ['Urgent badge', 'Red highlighting', 'Priority alerts', 'Quick sale focus'],
      category: 'visibility'
    },
    {
      id: 'analytics_pro',
      name: 'Advanced Analytics',
      description: 'Detailed insights on views, contacts, and performance metrics',
      icon: 'BarChart3',
      pricing: {
        motorcycle: 1200,
        car: 2500
      },
      benefits: ['Detailed metrics', 'Performance tracking', 'Competitor analysis', 'Optimization tips'],
      category: 'analytics'
    }
  ];

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const handleFeatureToggle = (featureId, checked) => {
    const newSelected = new Set(selectedFeatures);
    if (checked) {
      newSelected?.add(featureId);
    } else {
      newSelected?.delete(featureId);
    }
    setSelectedFeatures(newSelected);
    onToggleFeature(featureId, checked);
  };

  const isFeatureActive = (featureId) => {
    return activeFeatures?.some(f => f?.id === featureId);
  };

  const calculateTotal = (vehicleType) => {
    return Array.from(selectedFeatures)?.reduce((total, featureId) => {
      const feature = premiumFeatures?.find(f => f?.id === featureId);
      return total + (feature?.pricing?.[vehicleType] || 0);
    }, 0);
  };

  const groupedFeatures = premiumFeatures?.reduce((acc, feature) => {
    if (!acc?.[feature?.category]) {
      acc[feature.category] = [];
    }
    acc?.[feature?.category]?.push(feature);
    return acc;
  }, {});

  const categoryLabels = {
    visibility: 'Visibility & Promotion',
    trust: 'Trust & Credibility',
    media: 'Media & Content',
    analytics: 'Analytics & Insights'
  };

  const categoryIcons = {
    visibility: 'Eye',
    trust: 'Shield',
    media: 'Image',
    analytics: 'BarChart'
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground mb-2 flex items-center">
          <Icon name="Crown" size={24} className="mr-2 text-accent" />
          Premium Features
        </h2>
        <p className="text-muted-foreground">Enhance your listings with premium features to attract more buyers</p>
      </div>
      {/* Feature Categories */}
      <div className="space-y-6">
        {Object.entries(groupedFeatures)?.map(([category, features]) => (
          <div key={category}>
            <div className="flex items-center space-x-2 mb-4">
              <Icon name={categoryIcons?.[category]} size={20} className="text-primary" />
              <h3 className="font-medium text-foreground">{categoryLabels?.[category]}</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {features?.map((feature) => (
                <div
                  key={feature?.id}
                  className={`border rounded-lg p-4 transition-all duration-200 ${
                    selectedFeatures?.has(feature?.id)
                      ? 'border-primary bg-primary/5'
                      : isFeatureActive(feature?.id)
                      ? 'border-success bg-success/5' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedFeatures?.has(feature?.id) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                      }`}>
                        <Icon name={feature?.icon} size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{feature?.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{feature?.description}</p>
                      </div>
                    </div>
                    <Checkbox
                      checked={selectedFeatures?.has(feature?.id)}
                      onChange={(e) => handleFeatureToggle(feature?.id, e?.target?.checked)}
                      disabled={isFeatureActive(feature?.id)}
                    />
                  </div>

                  {/* Pricing */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="text-center p-2 bg-muted/50 rounded">
                      <p className="text-xs text-muted-foreground">Motorcycle</p>
                      <p className="font-semibold text-foreground">{formatAmount(feature?.pricing?.motorcycle)}</p>
                    </div>
                    <div className="text-center p-2 bg-muted/50 rounded">
                      <p className="text-xs text-muted-foreground">Car</p>
                      <p className="font-semibold text-foreground">{formatAmount(feature?.pricing?.car)}</p>
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="space-y-1">
                    {feature?.benefits?.slice(0, 2)?.map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Icon name="Check" size={12} className="text-success" />
                        <span className="text-xs text-muted-foreground">{benefit}</span>
                      </div>
                    ))}
                    {feature?.benefits?.length > 2 && (
                      <p className="text-xs text-muted-foreground">+{feature?.benefits?.length - 2} more benefits</p>
                    )}
                  </div>

                  {/* Active Badge */}
                  {isFeatureActive(feature?.id) && (
                    <div className="mt-3 flex items-center space-x-2 text-success">
                      <Icon name="CheckCircle" size={14} />
                      <span className="text-xs font-medium">Currently Active</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Pricing Summary */}
      {selectedFeatures?.size > 0 && (
        <div className="mt-6 p-4 bg-accent/5 border border-accent/20 rounded-lg">
          <h3 className="font-medium text-foreground mb-3">Selected Features Summary</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="p-3 bg-card border border-border rounded">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Motorcycle Listings</span>
                <Icon name="Bike" size={16} className="text-muted-foreground" />
              </div>
              <p className="text-lg font-bold text-foreground">{formatAmount(calculateTotal('motorcycle'))}</p>
              <p className="text-xs text-muted-foreground">per month</p>
            </div>
            
            <div className="p-3 bg-card border border-border rounded">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Car Listings</span>
                <Icon name="Car" size={16} className="text-muted-foreground" />
              </div>
              <p className="text-lg font-bold text-foreground">{formatAmount(calculateTotal('car'))}</p>
              <p className="text-xs text-muted-foreground">per month</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="default"
              onClick={() => onUpgrade(Array.from(selectedFeatures))}
              iconName="Crown"
              iconPosition="left"
              iconSize={16}
              className="flex-1"
            >
              Activate Selected Features
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedFeatures(new Set())}
              iconName="X"
              iconPosition="left"
              iconSize={16}
            >
              Clear Selection
            </Button>
          </div>
        </div>
      )}
      {/* Feature Comparison */}
      <div className="mt-6 p-4 bg-muted/25 rounded-lg">
        <h3 className="font-medium text-foreground mb-3 flex items-center">
          <Icon name="Info" size={16} className="mr-2 text-primary" />
          Why Choose Premium Features?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start space-x-2">
            <Icon name="TrendingUp" size={16} className="text-success mt-0.5" />
            <div>
              <p className="font-medium text-foreground">5x More Visibility</p>
              <p className="text-muted-foreground">Premium listings get significantly more views</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="Clock" size={16} className="text-success mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Faster Sales</p>
              <p className="text-muted-foreground">Sell 3x faster with premium features</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="Shield" size={16} className="text-success mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Build Trust</p>
              <p className="text-muted-foreground">Professional badges increase buyer confidence</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumFeatures;